import { createFileRoute, useHydrated } from '@tanstack/react-router'
import { pageMeta } from '~/lib/site/seo'
import { cacheHeaders } from '~/lib/site/cache'
import { Button } from '~/components/ui/Button'
import { bodyClass } from '~/components/ui/BodyText'
import { Container } from '~/components/ui/Container'
import { TextLink } from '~/components/ui/TextLink'
import { Img } from '~/components/ui/Img'
import { TabPanel, Tabs } from '~/components/ui/Tabs'
import handbook from '~/assets/images/resources-handbook.png?responsive'
import rightToRescue from '~/assets/images/placard-right-to-rescue.jpg?responsive'
import californiaCruelty from '~/assets/images/placard-real-california-cruelty.jpg?responsive'
import endFactoryFarming from '~/assets/images/placard-end-factory-farming.jpg?responsive'
import untilEveryAnimalIsFree from '~/assets/images/placard-until-every-animal-is-free.png?responsive'
import stopViolence from '~/assets/images/placard-stop-violence.png?responsive'
import { PageTitle } from '~/components/ui/PageTitle'
import { PageHero } from '~/components/ui/PageHero'

const TITLE = 'Resources for Activists | Direct Action Everywhere'
const DESCRIPTION =
  'Whether you are organizing your first action or you’re helping your chapter get to the next level, the Organizer’s Hub has all the resources you need!'

const tabs = [
  { id: 'materials', label: 'Materials' },
  { id: 'library', label: 'Resource library' },
  { id: 'handbook', label: 'Handbook' },
] as const
type Tab = (typeof tabs)[number]['id']

export const Route = createFileRoute('/resources')({
  // /resources?tab=library. The static host has a single /resources page, so
  // the tab from the URL is applied after hydration (the default tab, which
  // has no param, is what's prerendered).
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } => ({
    tab: tabs.find((t) => t.id === search.tab && t.id !== 'materials')?.id,
  }),
  headers: () => cacheHeaders.content,
  head: () => pageMeta({ title: TITLE, description: DESCRIPTION }),
  component: Resources,
})

const placards = [
  {
    image: rightToRescue,
    alt: 'Right to Rescue placard',
    pdf: 'https://media.directactioneverywhere.com/media/0644edfc07af42a6/Right-to-Rescue-placard-11x17.pdf',
  },
  {
    image: californiaCruelty,
    alt: 'Real California Cruelty placard',
    pdf: 'https://media.directactioneverywhere.com/media/30ea0e47a04857d5/Real-California-Cruelty.pdf',
  },
  {
    image: endFactoryFarming,
    alt: 'End Factory Farming placard',
    pdf: 'https://media.directactioneverywhere.com/media/08423d452d458097/End-Factory-Farming-placard.pdf',
  },
  {
    image: untilEveryAnimalIsFree,
    alt: 'Until every animal is free placard',
    pdf: 'https://media.directactioneverywhere.com/media/f246e3cb3ba92fd3/UEAIF-Letter-Placard-Template-2020.pdf',
  },
  {
    image: stopViolence,
    alt: 'Stop violence placard',
    pdf: 'https://media.directactioneverywhere.com/media/64d6720ddee88291/STOP-VIOLENCE-Letter-Placard-Template-2020.pdf',
  },
]

const library = [
  [
    'Vision & Strategy:',
    'https://drive.google.com/drive/folders/1fG9WlrrZ3urtq5Fp-nHQbZ8NC4QnaigN?usp=sharing',
    'DxE’s big picture vision and our theory for how we are going to get from where we are now to animal liberation.',
  ],
  [
    'Team Management & Relationships:',
    'https://drive.google.com/drive/folders/1BoD2KUGt2sM1By-WqOVlOMZqx2XrSoIt?usp=sharing',
    'How to effectively lead a team to achieve results.',
  ],
  [
    'Protest & Demonstrations:',
    'https://drive.google.com/drive/folders/1MTWmx0uHn63EFGzca5QyhaqKJVBeFvrY?usp=sharing',
    'Resources for organizing and taking on roles at protests.',
  ],
  [
    'Event Organizing:',
    'https://drive.google.com/drive/folders/1Awti0QRyOoN0XlHSE3n_MaZ74a4rchHw?usp=sharing',
    'The basics of organizing non-protest events.',
  ],
  [
    'Press and Social Media:',
    'https://drive.google.com/drive/folders/1fLRWqFf8wft2uTulkEL97dlQuGkeV__E?usp=sharing',
    'Maximize the reach of your actions and campaigns through media.',
  ],
  [
    'Legal Resources:',
    'https://drive.google.com/drive/folders/11h_gbM66q1T3fUVrB4hF6qI8HnwCzq2b?usp=sharing',
    'All resources related to police, civil disobedience, and the legal system.',
  ],
  [
    'Investigations:',
    'https://drive.google.com/drive/folders/1pz410BztUbAxEAKslImC3PcyTiIhggkS?usp=sharing',
    'How to investigate animal abusing industries, plus key footage from past investigations.',
  ],
  [
    'Personal Organization:',
    'https://drive.google.com/drive/folders/1czTXoa0UHYJH6UD1wXFw63vuO_6wYGYJ?usp=sharing',
    'Tips for managing our time well and being organized and dependable so we can achieve more for animals and become better leaders.',
  ],
  [
    'Other',
    'https://drive.google.com/drive/folders/146dSeY0L0Dk5VAQ6D7G8YCAO9KOtfyM6?usp=sharing',
    '',
  ],
] as const

const h2Class =
  'mt-0 mb-2.5 text-[28px] leading-[34px] font-extrabold text-fg md:text-[35px] md:leading-[42px]'
function Resources() {
  const hydrated = useHydrated()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const tab = (hydrated && search.tab) || 'materials'
  return (
    <PageHero as="div" bottom="page">
      <Container>
        <div className="mx-auto max-w-[700px] text-center">
          <PageTitle size="md" className="mb-5">
            Resources for activists
          </PageTitle>
          <p className={`${bodyClass} mb-8`}>
            Whether you are organizing your first action or you’re helping your
            chapter get to the next level, the Organizer’s Hub has all the
            resources you need!
          </p>
        </div>

        <Tabs
          id="resources"
          tabs={tabs}
          value={tab}
          onChange={(t) =>
            navigate({
              search: (prev) => ({
                ...prev,
                tab: t === 'materials' ? undefined : t,
              }),
              replace: true,
              resetScroll: false,
            })
          }
          label="Resources"
          className="mb-2"
        />

        <TabPanel id="resources" tab="materials" value={tab}>
          <Materials />
        </TabPanel>
        <TabPanel id="resources" tab="library" value={tab}>
          <Library />
        </TabPanel>
        <TabPanel id="resources" tab="handbook" value={tab}>
          <Handbook />
        </TabPanel>
      </Container>
    </PageHero>
  )
}

function Block({ children }: { children: React.ReactNode }) {
  return (
    <section className="border-b border-line py-[60px]">{children}</section>
  )
}

function Materials() {
  return (
    <div>
      <Block>
        <h2 className={h2Class}>Logos</h2>
        <p className={bodyClass}>
          You can find our logos in this Dropbox folder
        </p>
        <img
          src="/images/logo-dxe-dark.png"
          alt="DxE logo"
          width={330}
          height={44}
          className="mb-5 max-w-full"
        />
        <Button
          href="https://www.dropbox.com/sh/2kcfqbb0jyntrj8/AACXbPCwo5NIfxA19FSNb7Taa?st=ihc10iy7&dl=0"
          target="_blank"
          rel="noreferrer"
        >
          Open folder with logos
        </Button>
      </Block>

      <Block>
        <h2 className={h2Class}>Placard Generator</h2>
        <p className={bodyClass}>Create your own placards for DxE actions</p>
        <iframe
          title="Placard generator"
          src="https://dxe-static.s3-us-west-1.amazonaws.com/placard-generator/index.html"
          loading="lazy"
          className="h-[800px] w-full border-0"
        />
        <div className="mt-5 text-center">
          <Button
            href="https://ec2.dxe.io/placard-generator/"
            target="_blank"
            rel="noreferrer"
            variant="outline"
          >
            Open generator in new tab
          </Button>
        </div>
      </Block>

      <Block>
        <h2 className={h2Class}>Placard Generator for Rose's Law</h2>
        <p className={bodyClass}>
          Create your own placards for actions in support of{' '}
          <TextLink href="https://www.roseslaw.org/">Rose's Law</TextLink>
        </p>
        <iframe
          title="Rose's Law placard generator"
          src="https://dxe-static.s3-us-west-1.amazonaws.com/rl-placard-generator/index.html"
          loading="lazy"
          className="h-[800px] w-full border-0"
        />
        <div className="mt-5 text-center">
          <Button
            href="https://gen.roseslaw.org/"
            target="_blank"
            rel="noreferrer"
            variant="outline"
          >
            Open generator in new tab
          </Button>
        </div>
      </Block>

      <Block>
        <h2 className={h2Class}>Additional Placards</h2>
        <p className={bodyClass}>
          Here are additional placards you can use in various contexts
        </p>
        <ul className="m-0 grid list-none gap-5 p-0 md:grid-cols-3">
          {placards.map((p) => (
            <li key={p.alt}>
              <a
                href={p.pdf}
                target="_blank"
                rel="noreferrer"
                title="Download PDF"
              >
                <Img
                  image={p.image}
                  sizes="(min-width: 992px) 300px, (min-width: 768px) 28vw, 90vw"
                  alt={p.alt}
                  className="w-full"
                />
              </a>
            </li>
          ))}
        </ul>
      </Block>

      <p className={`${bodyClass} mt-10 mb-0 text-center`}>
        DxE's model is open-source. If you follow our{' '}
        <TextLink href="/core-values">core values</TextLink> and take action
        towards our mission, you are DxE.
        <br />
        The above assets are licensed under{' '}
        <TextLink href="https://creativecommons.org/licenses/by-sa/4.0/">
          Creative Commons BY-SA 4.0
        </TextLink>{' '}
        and do not require attribution.
      </p>
    </div>
  )
}

function Library() {
  return (
    <div className="mx-auto max-w-[800px] py-[60px]">
      <h2 className={h2Class}>Resource Library</h2>
      <p className={`${bodyClass} mb-5`}>
        This library is a collection of resources to help you be the best
        activist and organizer you can be - both inside and outside the DxE
        network.
      </p>
      <p className={`${bodyClass} mb-5`}>
        The content is broken down into main folders:
      </p>
      <ol className={`${bodyClass} mb-8 list-decimal space-y-2 pl-6`}>
        {library.map(([name, href, desc]) => (
          <li key={name}>
            <TextLink href={href}>{name}</TextLink>
            {desc && ` ${desc}`}
          </li>
        ))}
      </ol>
      <Button
        href="https://drive.google.com/open?id=1byEmJOb_ngm2zLfifRll9f0UFxrGTJwX"
        target="_blank"
        rel="noreferrer"
        className="mb-8"
      >
        View Resource Library on Google Drive
      </Button>
      <p className={`${bodyClass} mb-5`}>
        If you’re new, all this information might be a bit overwhelming. There
        is no need to read everything before getting started. In fact, the best
        way to learn is to do, so get out there as soon as you can!
      </p>
      <p className={`${bodyClass} mb-0`}>
        If you have a request for a specific resource and cannot find it, or any
        feedback on the resource library, please email{' '}
        <TextLink href="mailto:development@directactioneverywhere.com">
          development@directactioneverywhere.com
        </TextLink>
        .
      </p>
    </div>
  )
}

function Handbook() {
  return (
    <div className="mx-auto max-w-[800px] py-[60px] text-center">
      <p className={`${bodyClass} mb-5`}>
        This is the handbook for the San Francisco Bay chapter
      </p>
      <a href="https://dxe.io/handbook" target="_blank" rel="noreferrer">
        <Img
          image={handbook}
          sizes="287px"
          alt="DxE Chapter member handbook"
          className="mx-auto mb-5 w-[287px]"
        />
      </a>
      <Button href="https://dxe.io/handbook" target="_blank" rel="noreferrer">
        Download PDF
      </Button>
    </div>
  )
}
